import { DeleteTwoTone, EditOutlined, EyeOutlined, MoreOutlined } from "@ant-design/icons";
import { Affix, Button, Card, Dropdown, Form, Menu, Popconfirm, Select } from "antd";
import ListTable from "../../../common/ListTable";
import { Fragment, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { BOOKING_CARTON_LIST, BOOKING_CARTON_DELETE } from "../../../apiServices/API_ENDPOINTS";
import { deleteData, getData } from "../../../apiServices/common";
import { alertPop } from "../../../apiServices/common/helper";
import {
  BOOKING_CARTON_CREATE_PERMISSION,
  BOOKING_CARTON_DELETE_PERMISSION,
  BOOKING_CARTON_EDIT_PERMISSION,
  BOOKING_CARTON_VIEW_PERMISSION,
} from "../../../routes/permissions";
import { formatNumberWithCommas, hasPermission } from "../../../utils/functions";
import { isMobileView } from "../../Common/AppHelper";
import BodyHeader from "../../Common/BodyHeader";
import CustomFilter from "../../Common/CustomFilter";
import Header from "../../Common/Header";

export default function CartonBooking(props) {
  // Props
  const { width, viewMode } = props;

  // Router
  const location = useLocation();
  const navigate = useNavigate();
  const routerPath = location ? location.pathname : null;

  // States
  const [showFilter, setShowFilter] = useState(
    localStorage.getItem("filter_status") && routerPath
      ? JSON.parse(localStorage.getItem("filter_status"))[routerPath]
      : false,
  );
  const [permitList, setPermitList] = useState();
  const [totalData, setTotalData] = useState();
  const [currentPage, setCurrentPage] = useState(1);
  const [searchValues, setSearchValues] = useState();
  const [loading, setLoading] = useState(false);
  const [rangeData, setRangeData] = useState();
  const [fetchError, setFetchError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Redux
  const filterData = useSelector(({ filter }) => filter.filterData);

  // Antd Constants
  const { Option } = Select;
  const [form] = Form.useForm();

  // Filter open/close status update in localStorage
  useEffect(() => {
    const filterStatus = localStorage.getItem("filter_status");
    const parsedFilterStatus = filterStatus ? JSON.parse(filterStatus) : {};

    if (showFilter) {
      parsedFilterStatus[routerPath] = true;
      localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
    } else {
      parsedFilterStatus[routerPath] = false;
      localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
    }
  }, [showFilter, routerPath, location]);

  const searchFilter = (values) => {
    console.log("values", values);
  };

  const columns = [
    {
      title: "Supplier PO Number",
      dataIndex: ["supplier_po_number"],
      key: "supplier_po_number",
    },
    {
      title: "Booking Date",
      dataIndex: ["booking_date"],
      key: "booking_date",
    },
    {
      title: "Supplier Name",
      dataIndex: ["supplier", "name"],
      key: "supplier_name",
      width: "10%",
    },
    {
      title: "Sales Contract",
      dataIndex: ["sales_contract", "reference_no"],
      key: "sales_contract_no",
    },
    {
      title: "Buyer",
      dataIndex: ["buyer", "name"],
      key: "buyer_name",
    },
    {
      title: "Buying Office",
      dataIndex: ["buying_office", "name"],
      key: "buying_office_name",
    },
    {
      title: "Status",
      dataIndex: ["status"],
      key: "status",
      align: "center",
    },
    {
      title: "Action",
      key: "action",
      align: "center",
      render: (_, record) => (
        <div className="cursore-pointer">
          <Dropdown overlay={menu(record)}>
            <MoreOutlined style={{ fontSize: "20px" }} />
          </Dropdown>
        </div>
      ),
    },
  ];

  const getAllPermitList = async (passedObject) => {
    const filter = passedObject || {};
    const { page: filterPage, ...requestFilter } = filter;
    setFetchError(null);
    const page = filterPage ?? currentPage;
    const customQuery = `${BOOKING_CARTON_LIST}?page=${page}`;
    const response = await getData(customQuery, false, requestFilter);

    if (response && response?.data?.code === 200) {
      const paginated = response?.data?.data;
      setPermitList(paginated?.data ?? paginated ?? []);
      setTotalData(paginated?.total ?? 0);
      setRangeData(paginated?.per_page ?? 10);
    } else {
      setFetchError("Could not load this list.");
    }
    setLoading(false);
  };

  useEffect(() => {
    setLoading(true);
    getAllPermitList(filterData);
  }, [currentPage, searchValues, filterData]);

  const removeReconciliation = async (value) => {
    const response = await deleteData(BOOKING_CARTON_DELETE(value));

    if (response && (response?.data?.code === 200 || response?.code === 200)) {
      alertPop("success", "Deleted Successfully!");
      getAllPermitList({ page: currentPage });
    } else {
      alertPop("error", response?.data?.message?.[0] || response?.data?.message || "Something Went Wrong!");
    }
  };

  const openForShow = (id) => {
    navigate(`/carton-booking/view/${id}`);
  };

  const menu = (data) => (
    <Menu
      items={[
        {
          label: (
            <Link to={`/carton-booking/edit/${data.id}`}>
              <EditOutlined /> Edit
            </Link>
          ),
          key: "0",
          disabled: !hasPermission([BOOKING_CARTON_EDIT_PERMISSION]),
        },
        {
          label: (
            <Link to={`/carton-booking/view/${data.id}`}>
              <EyeOutlined /> View
            </Link>
          ),
          key: "1",
          disabled: !hasPermission([BOOKING_CARTON_VIEW_PERMISSION]),
        },
        {
          label: (
            <Popconfirm title="Are you sure you want to delete?" onConfirm={() => removeReconciliation(data?.id)}>
              <DeleteTwoTone /> Delete
            </Popconfirm>
          ),
          key: "2",
          disabled: !hasPermission([BOOKING_CARTON_DELETE_PERMISSION]),
        },
      ]}
    />
  );

  const paginate = (page) => {
    setCurrentPage(page);
  };

  const handleFilter = () => {
    setShowFilter((prev) => !prev);
  };

  const handleOk = () => {
    form.submit();
  };

  const handleCancel = () => {
    form.resetFields();
    setIsModalOpen(false);
  };

  const memoizedCustomFilter = useMemo(
    () => (
      <CustomFilter
        showFilter={showFilter}
        tableData={permitList}
        fetchTableData={getAllPermitList}
        filterName="local_purchase_reconciliation"
      />
    ),
    [showFilter],
  );

  return (
    <Fragment>
      <Affix offsetTop={0}>
        <Card className="p-fixed" style={{ width: `${isMobileView() ? "100%" : `${width}px`}` }}>
          <Header
            title="Carton & Poly Booking"
            subTitle=""
            actions={[
              <Button
                type={showFilter ? "danger" : "primary"}
                size="small"
                onClick={handleFilter}
                style={{ float: "right" }}
              >
                {`${showFilter ? "Close" : "Open"} Filter Option`}
              </Button>,
              <Button key="3" size="small" type="primary" disabled={!hasPermission([BOOKING_CARTON_CREATE_PERMISSION])}>
                <Link to="/carton-booking/create">
                  {hasPermission([BOOKING_CARTON_CREATE_PERMISSION]) ? "Add New" : "No Permission"}
                </Link>
              </Button>,
            ]}
          ></Header>
        </Card>
      </Affix>

      <div className="containt-body">
        <Card>
          <BodyHeader
            // title="Style List"
            // filtersOption={filtersOption}
            searchFilter={searchFilter}
          />
          {memoizedCustomFilter}
          <ListTable
            bordered
            loading={loading}
            error={fetchError}
            onRetry={() => getAllPermitList(filterData)}
            emptyText="No records to show yet."
            dataSource={permitList}
            columns={columns}
            pagination={{
              showSizeChanger: false,
              current: currentPage,
              total: totalData,
              pageSize: rangeData,
              onChange: (page) => paginate(page),
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
            }}
          />
        </Card>
      </div>
    </Fragment>
  );
}
